<template>
  <!--
    BrowseDrill — the NARROW (iPad-portrait / phone) browse for the insurance
    catalog. Where the Miller columns show many levels side-by-side, this shows
    exactly ONE level at a time (the children of the current node) plus a
    breadcrumb of NAME chips to walk back up the trail.

    Touch-first: rows are >=48px, breadcrumb chips are >=44px tap targets, and
    everything is a single column of names — there is NEVER any horizontal
    scroll. Purely presentational: no Vuex, no API, no element-ui; driven
    entirely by props and emits.

    Tap rules (see methods + the report):
      - a row WITH children  → `drill(node)`  (descend) AND `select(node)`
                               (so the inspector follows you down)
      - a leaf row           → `select(node)` only
      - the root home chip    → `up(null)`
      - any breadcrumb crumb  → `up(node)`     (jump straight to that ancestor)
      - the "back" affordance  → `up(parent)`   (parent = 2nd-to-last crumb,
                                                  or null when one level deep)
  -->
  <div class="bd">
    <!-- ───────────────────────── breadcrumb bar ───────────────────────── -->
    <nav class="bd__crumbs" aria-label="breadcrumb">
      <!-- back affordance: one step up the trail -->
      <button
        type="button"
        class="bd__back"
        :disabled="breadcrumb.length === 0"
        @click="goBack"
      >
        <span class="bd__back-arrow" aria-hidden="true">&lsaquo;</span>
        <span class="bd__back-label">{{ $t("lmg.insure_browse_back") }}</span>
      </button>

      <div class="bd__crumbs-trail">
        <!-- root / home -->
        <button
          type="button"
          class="bd__crumb bd__crumb--home"
          :class="{ 'is-current': breadcrumb.length === 0 }"
          :aria-current="breadcrumb.length === 0 ? 'page' : null"
          :title="$t('lmg.insure_browse_root')"
          @click="onUp(null)"
        >
          <span class="bd__crumb-home-icon" aria-hidden="true">&#8962;</span>
          <span class="bd__crumb-home-text">{{
            $t("lmg.insure_browse_root")
          }}</span>
        </button>

        <template v-for="(item, idx) in displayCrumbs">
          <span
            :key="'sep-' + idx"
            class="bd__crumb-sep"
            aria-hidden="true"
          >&rsaquo;</span>

          <!-- collapsed-middle ellipsis crumb -->
          <span
            v-if="item.ellipsis"
            :key="'ell-' + idx"
            class="bd__crumb-wrap"
          >
            <button
              type="button"
              class="bd__crumb bd__crumb--more"
              :aria-label="$t('lmg.insure_browse_more_crumbs')"
              :aria-expanded="String(morePopoverOpen)"
              @click.stop="toggleMorePopover"
            >&hellip;</button>

            <!-- popover listing the hidden ancestors -->
            <ul
              v-if="morePopoverOpen"
              class="bd__more-pop"
              role="menu"
            >
              <li
                v-for="hid in item.hidden"
                :key="hid.ID"
                class="bd__more-item"
                role="menuitem"
              >
                <button
                  type="button"
                  class="bd__more-btn"
                  :title="hid.Name"
                  @click="onCrumbPick(hid)"
                >{{ displayName(hid.Name, stripPrefix) }}</button>
              </li>
            </ul>
          </span>

          <!-- a real crumb -->
          <button
            v-else
            :key="'crumb-' + item.node.ID"
            type="button"
            class="bd__crumb"
            :class="{ 'is-current': item.isCurrent }"
            :aria-current="item.isCurrent ? 'page' : null"
            :title="item.node.Name"
            @click="onUp(item.node)"
          >{{ displayName(item.node.Name, stripPrefix) }}</button>
        </template>
      </div>
    </nav>

    <!-- ───────────────────────── current level list ───────────────────── -->
    <div class="bd__list" role="tree">
      <ins-node-row
        v-for="node in currentChildren"
        :key="node.ID"
        :node="node"
        :has-children="childCountOf(node.ID) > 0"
        :selected="node.ID === selectedId"
        :expanded="false"
        :strip-prefix="stripPrefix"
        :hide-type-chip="hideTypeChip"
        @select="onRowTap(node)"
        @toggle="onRowToggle(node)"
      />

      <!-- empty state: a node with no children (a true leaf reached) -->
      <div
        v-if="currentChildren.length === 0"
        class="bd__empty"
      >{{ $t("lmg.insure_browse_empty") }}</div>
    </div>
  </div>
</template>

<script>
import InsNodeRow from "./InsNodeRow.vue";
import { displayName } from "./insName";

// How many crumbs to keep before collapsing the middle. We always keep the
// FIRST crumb and the LAST `KEEP_TAIL` crumbs; anything in between folds into
// a single "…" popover crumb. Threshold = first + tail + at least 1 hidden.
const KEEP_TAIL = 2;

export default {
  name: "browse-drill",
  components: { InsNodeRow },
  props: {
    // root → current node, the navigated trail. Empty array = at root level.
    breadcrumb: { type: Array, default: () => [] },
    // the rows to render at THIS level (children of the current node, or the
    // roots when breadcrumb is empty).
    currentChildren: { type: Array, default: () => [] },
    selectedId: { type: Number, default: null },
    // (id) => Number : how many children a node has, used to mark drillables.
    childCountOf: { type: Function, default: () => 0 },
    // DISPLAY-ONLY: hide leading outline numbers from shown names.
    stripPrefix: { type: Boolean, default: false },
    // DISPLAY-ONLY: hide the per-row dataType chip (passed through to rows).
    hideTypeChip: { type: Boolean, default: false },
  },
  data() {
    return {
      morePopoverOpen: false,
    };
  },
  computed: {
    // The parent of the current node = second-to-last crumb (or null = root).
    backTarget() {
      const n = this.breadcrumb.length;
      if (n <= 1) return null; // one level deep → back goes to root
      return this.breadcrumb[n - 2];
    },
    // Crumbs to render, with the middle collapsed when the trail is long.
    // Each entry is either { node, isCurrent } or { ellipsis: true, hidden: [] }.
    displayCrumbs() {
      const trail = this.breadcrumb;
      const n = trail.length;
      const lastIdx = n - 1;
      const asItem = (node, i) => ({ node, isCurrent: i === lastIdx });

      // Short enough to show every crumb: first + tail + >=1 hidden needed to
      // bother collapsing.
      if (n <= 1 + KEEP_TAIL + 1) {
        return trail.map(asItem);
      }

      const first = asItem(trail[0], 0);
      const hidden = trail.slice(1, n - KEEP_TAIL); // the folded-away middle
      const tail = trail
        .slice(n - KEEP_TAIL)
        .map((node, k) => asItem(node, n - KEEP_TAIL + k));

      return [first, { ellipsis: true, hidden }, ...tail];
    },
  },
  watch: {
    // Navigating anywhere closes the hidden-crumbs popover.
    breadcrumb() {
      this.morePopoverOpen = false;
    },
  },
  methods: {
    displayName,
    // ── row taps ─────────────────────────────────────────────────────────
    // Body tap: leaf → select; parent → drill AND select (inspector follows).
    onRowTap(node) {
      if (this.childCountOf(node.ID) > 0) {
        this.$emit("select", node);
        this.$emit("drill", node);
      } else {
        this.$emit("select", node);
      }
    },
    // Chevron tap on a parent row: drill only (don't move the selection on a
    // pure "open this branch" gesture, mirroring InsNodeRow's toggle intent).
    onRowToggle(node) {
      this.$emit("drill", node);
    },

    // ── breadcrumb navigation ────────────────────────────────────────────
    onUp(node) {
      this.morePopoverOpen = false;
      this.$emit("up", node); // node, or null for root
    },
    onCrumbPick(node) {
      this.morePopoverOpen = false;
      this.$emit("up", node);
    },
    goBack() {
      if (this.breadcrumb.length === 0) return;
      this.onUp(this.backTarget);
    },
    toggleMorePopover() {
      this.morePopoverOpen = !this.morePopoverOpen;
    },
  },
};
</script>

<style scoped>
.bd {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: var(--c-surface, #fff);
  color: var(--c-text, #2c3e50);
  font-family: var(--c-font, inherit);
}

/* ── breadcrumb bar ──────────────────────────────────────────────────── */
.bd__crumbs {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--c-border, #e4e9f1);
  background: var(--c-surface, #fff);
}

.bd__back {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 14px 0 8px;
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 999px;
  background: var(--c-surface, #fff);
  color: var(--c-blue, #3b6ea8);
  font-size: 14px;
  cursor: pointer;
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease);
}
.bd__back:hover:not(:disabled) {
  background: var(--c-blue-soft, #ebf2fa);
}
.bd__back:disabled {
  opacity: 0.4;
  cursor: default;
  color: var(--c-text-soft, #5e6c80);
}
.bd__back-arrow {
  font-size: 18px;
  line-height: 1;
}

/* the trail wraps (never scrolls sideways) so the active leaf is always shown */
.bd__crumbs-trail {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 2px;
}

.bd__crumb {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  max-width: 100%;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--c-text-soft, #5e6c80);
  font-size: 14px;
  line-height: 1.2;
  cursor: pointer;
  /* names wrap rather than truncate — Thai has no word spaces, so allow
     break-anywhere so a long name folds instead of overflowing. */
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: left;
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease),
    color var(--c-fast, 140ms) var(--c-ease, ease);
}
.bd__crumb:hover {
  background: var(--c-blue-soft, #ebf2fa);
  color: var(--c-blue, #3b6ea8);
}
.bd__crumb.is-current {
  color: var(--c-text, #2c3e50);
  font-weight: 600;
  cursor: default;
}
.bd__crumb.is-current:hover {
  background: transparent;
}

.bd__crumb--home {
  gap: 6px;
  padding-left: 10px;
}
.bd__crumb-home-icon {
  font-size: 16px;
  line-height: 1;
}

.bd__crumb-sep {
  flex: 0 0 auto;
  color: var(--c-border, #e4e9f1);
  font-size: 14px;
  user-select: none;
}

/* collapsed-middle "…" crumb + its popover */
.bd__crumb-wrap {
  position: relative;
  display: inline-flex;
}
.bd__crumb--more {
  min-width: 44px;
  justify-content: center;
  font-size: 18px;
  letter-spacing: 1px;
}
.bd__more-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 180px;
  max-width: 280px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--c-surface, #fff);
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(44, 62, 80, 0.16);
}
.bd__more-item {
  margin: 0;
}
.bd__more-btn {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--c-text, #2c3e50);
  font-size: 14px;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease);
}
.bd__more-btn:hover {
  background: var(--c-blue-soft, #ebf2fa);
  color: var(--c-blue, #3b6ea8);
}

/* ── current level list ──────────────────────────────────────────────── */
.bd__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden; /* never sideways scroll */
}
/* iPad/phone touch height: bump InsNodeRow's 44px minimum up to >=48px here */
.bd__list >>> .ins-row {
  min-height: 48px;
}
.bd__empty {
  padding: 24px 16px;
  color: var(--c-text-soft, #5e6c80);
  font-size: 14px;
  text-align: center;
}
</style>
